/** Components for `./ask-ai-shell`, split out so that module can stay a plain `.ts`. */
import * as React from "react";
import { useFeatureFlag } from "@authe/ui-core/flags";
import { FEATURE_FLAG } from "@authe/fe-commons/features";
import { type AskAiContextValue, AskAiContext } from "./ask-ai-shell";

const AskAiDrawer = React.lazy(() => import("@/components/shell/AskAiDrawer").then((module) => ({ default: module.AskAiDrawer })));

export function AskAiProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const enabled = useFeatureFlag(FEATURE_FLAG.askAi);
  const [open, setOpen] = React.useState(false);
  const [requested, setRequested] = React.useState(false);
  const openAskAi = React.useCallback(() => {
    if (enabled) { setRequested(true); setOpen(true); }
  }, [enabled]);

  // Switched off mid-session (an operator, or a tenancy switch): close, so
  // switching it back on later does not reopen a drawer nobody asked for.
  React.useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  const value = React.useMemo<AskAiContextValue>(() => ({ enabled, openAskAi }), [enabled, openAskAi]);

  return (
    <AskAiContext.Provider value={value}>
      {children}
      {enabled && requested ? <React.Suspense fallback={<p role="status">Opening assistant…</p>}><AskAiDrawer open={open} onOpenChange={setOpen} /></React.Suspense> : null}
    </AskAiContext.Provider>
  );
}
